import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  AgbRegisterImportPayload,
  AgbRegisterImportResult,
  AgbRegisterPracticeLookup,
  AgbRegisterPracticeRelations,
  CollectionResponse,
  LdfBackfillOptions,
  OrganizationLocation,
  Practice,
  PracticeDetail,
  PracticeLdfPayload,
  PracticePayload,
  PracticeZorgmailPayload,
  QualityOrganizationSubmission,
  QualityOrganizationSubmissionDetail,
  Response,
  ZorgtopicsDeliveryMonth,
} from "~/types/api";

export function createPracticeApi({
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
  getPaginationRequest,
}: ApiRequestHelpers) {
  const myOrganizationPractices = () => getRequest<CollectionResponse<Practice>>("/my-organization/practices");

  const myOrganizationPractice = (practiceId: string) =>
    getRequest<Response<PracticeDetail>>(`/my-organization/practices/${practiceId}`);

  const createMyOrganizationPractice = (data: PracticePayload) =>
    postRequest<Response<PracticeDetail>>("/my-organization/practices", data);

  const updateMyOrganizationPractice = (id: string, data: PracticePayload) =>
    putRequest<Response<PracticeDetail>>(`/my-organization/practices/${id}`, data);

  const assignPracticeToMyOrganizationLocation = (locationId: number, practiceId: string | null) =>
    putRequest<Response<OrganizationLocation>>(`/my-organization/locations/${locationId}/practice`, {
      practice_id: practiceId,
    });

  const saveMyOrganizationPracticeLdf = (practiceId: string, data: PracticeLdfPayload) =>
    putRequest<Response<PracticeDetail>>(`/my-organization/practices/${practiceId}/ldf`, data);

  const removeMyOrganizationPracticeLdf = (practiceId: string) =>
    deleteRequest<Response<PracticeDetail>>(`/my-organization/practices/${practiceId}/ldf`);

  const testMyOrganizationPracticeLdf = (practiceId: string) =>
    postRequest<Response<PracticeDetail>>(`/my-organization/practices/${practiceId}/ldf/test`);

  const organizationPractices = (organizationId: string) =>
    getRequest<CollectionResponse<Practice>>(`/organizations/${organizationId}/practices`);

  const organizationPractice = (organizationId: string, practiceId: string) =>
    getRequest<Response<PracticeDetail>>(`/organizations/${organizationId}/practices/${practiceId}`);

  const lookupOrganizationPracticeAgbRegister = (organizationId: string, agbCode: string) =>
    getRequest<Response<AgbRegisterPracticeLookup>>(`/organizations/${organizationId}/practices/agb-register`, {
      params: { agb_code: agbCode },
      suppressValidationToast: true,
    });

  const organizationPracticeAgbRegisterRelations = (organizationId: string, practiceId: string) =>
    getRequest<Response<AgbRegisterPracticeRelations>>(
      `/organizations/${organizationId}/practices/${practiceId}/agb-register`,
    );

  const importOrganizationPracticeAgbRegister = (
    organizationId: string,
    practiceId: string,
    data: AgbRegisterImportPayload,
  ) =>
    postRequest<Response<AgbRegisterImportResult>>(
      `/organizations/${organizationId}/practices/${practiceId}/agb-register`,
      data,
      { suppressValidationToast: true },
    );

  const createOrganizationPractice = (organizationId: string, data: PracticePayload) =>
    postRequest<Response<PracticeDetail>>(`/organizations/${organizationId}/practices`, data);

  const updateOrganizationPractice = (organizationId: string, id: string, data: PracticePayload) =>
    putRequest<Response<PracticeDetail>>(`/organizations/${organizationId}/practices/${id}`, data);

  const assignPracticeToOrganizationLocation = (
    organizationId: string,
    locationId: number,
    practiceId: string | null,
  ) =>
    putRequest<Response<OrganizationLocation>>(`/organizations/${organizationId}/locations/${locationId}/practice`, {
      practice_id: practiceId,
    });

  const saveOrganizationPracticeLdf = (organizationId: string, practiceId: string, data: PracticeLdfPayload) =>
    putRequest<Response<PracticeDetail>>(`/organizations/${organizationId}/practices/${practiceId}/ldf`, data);

  const removeOrganizationPracticeLdf = (organizationId: string, practiceId: string) =>
    deleteRequest<Response<PracticeDetail>>(`/organizations/${organizationId}/practices/${practiceId}/ldf`);

  const testOrganizationPracticeLdf = (organizationId: string, practiceId: string) =>
    postRequest<Response<PracticeDetail>>(`/organizations/${organizationId}/practices/${practiceId}/ldf/test`);

  const myOrganizationPracticeLdfSubmissions = (practiceId: string, page: number) =>
    getPaginationRequest<QualityOrganizationSubmission>(`/my-organization/practices/${practiceId}/ldf/submissions`, {
      number: page,
      size: 12,
    });

  const myOrganizationPracticeLdfSubmission = (practiceId: string, submissionId: string) =>
    getRequest<Response<QualityOrganizationSubmissionDetail>>(
      `/my-organization/practices/${practiceId}/ldf/submissions/${submissionId}`,
    );

  const organizationPracticeLdfSubmissions = (organizationId: string, practiceId: string, page: number) =>
    getPaginationRequest<QualityOrganizationSubmission>(
      `/organizations/${organizationId}/practices/${practiceId}/ldf/submissions`,
      { number: page, size: 12 },
    );

  const organizationPracticeLdfSubmission = (organizationId: string, practiceId: string, submissionId: string) =>
    getRequest<Response<QualityOrganizationSubmissionDetail>>(
      `/organizations/${organizationId}/practices/${practiceId}/ldf/submissions/${submissionId}`,
    );

  const myOrganizationPracticeLdfBackfill = (practiceId: string, from?: string) =>
    getRequest<Response<LdfBackfillOptions>>(`/my-organization/practices/${practiceId}/ldf/backfill`, {
      params: from ? { from } : {},
    });

  const startMyOrganizationPracticeLdfBackfill = (practiceId: string, from: string) =>
    postRequest<Response<LdfBackfillOptions>>(`/my-organization/practices/${practiceId}/ldf/backfill`, { from });

  const organizationPracticeLdfBackfill = (organizationId: string, practiceId: string, from?: string) =>
    getRequest<Response<LdfBackfillOptions>>(`/organizations/${organizationId}/practices/${practiceId}/ldf/backfill`, {
      params: from ? { from } : {},
    });

  const startOrganizationPracticeLdfBackfill = (organizationId: string, practiceId: string, from: string) =>
    postRequest<Response<LdfBackfillOptions>>(`/organizations/${organizationId}/practices/${practiceId}/ldf/backfill`, {
      from,
    });

  const myOrganizationPracticeZorgtopicsDeliveryPreview = (practiceId: string) =>
    getRequest<Response<ZorgtopicsDeliveryMonth[]>>(
      `/my-organization/practices/${practiceId}/zorgtopics/delivery-preview`,
    );

  const organizationPracticeZorgtopicsDeliveryPreview = (organizationId: string, practiceId: string) =>
    getRequest<Response<ZorgtopicsDeliveryMonth[]>>(
      `/organizations/${organizationId}/practices/${practiceId}/zorgtopics/delivery-preview`,
    );

  const setMyOrganizationPracticeIntakeQuestionnaires = (practiceId: string, enabled: boolean) =>
    postRequest<Response<PracticeDetail>>(
      `/my-organization/practices/${practiceId}/intake-questionnaires/${enabled ? "enable" : "disable"}`,
    );

  const setOrganizationPracticeIntakeQuestionnaires = (organizationId: string, practiceId: string, enabled: boolean) =>
    postRequest<Response<PracticeDetail>>(
      `/organizations/${organizationId}/practices/${practiceId}/intake-questionnaires/${enabled ? "enable" : "disable"}`,
    );

  const saveMyOrganizationPracticeZorgmail = (practiceId: string, data: PracticeZorgmailPayload) =>
    putRequest<Response<PracticeDetail>>(`/my-organization/practices/${practiceId}/zorgmail`, data);

  const removeMyOrganizationPracticeZorgmail = (practiceId: string) =>
    deleteRequest<Response<PracticeDetail>>(`/my-organization/practices/${practiceId}/zorgmail`);

  const activateMyOrganizationPracticeZorgmail = (practiceId: string) =>
    postRequest<Response<PracticeDetail>>(`/my-organization/practices/${practiceId}/zorgmail/activate`);

  const deactivateMyOrganizationPracticeZorgmail = (practiceId: string) =>
    postRequest<Response<PracticeDetail>>(`/my-organization/practices/${practiceId}/zorgmail/deactivate`);

  const saveOrganizationPracticeZorgmail = (organizationId: string, practiceId: string, data: PracticeZorgmailPayload) =>
    putRequest<Response<PracticeDetail>>(`/organizations/${organizationId}/practices/${practiceId}/zorgmail`, data);

  const removeOrganizationPracticeZorgmail = (organizationId: string, practiceId: string) =>
    deleteRequest<Response<PracticeDetail>>(`/organizations/${organizationId}/practices/${practiceId}/zorgmail`);

  const activateOrganizationPracticeZorgmail = (organizationId: string, practiceId: string) =>
    postRequest<Response<PracticeDetail>>(`/organizations/${organizationId}/practices/${practiceId}/zorgmail/activate`);

  const deactivateOrganizationPracticeZorgmail = (organizationId: string, practiceId: string) =>
    postRequest<Response<PracticeDetail>>(
      `/organizations/${organizationId}/practices/${practiceId}/zorgmail/deactivate`,
    );

  return {
    myOrganizationPractices,
    myOrganizationPractice,
    createMyOrganizationPractice,
    updateMyOrganizationPractice,
    assignPracticeToMyOrganizationLocation,
    organizationPractices,
    organizationPractice,
    lookupOrganizationPracticeAgbRegister,
    organizationPracticeAgbRegisterRelations,
    importOrganizationPracticeAgbRegister,
    createOrganizationPractice,
    updateOrganizationPractice,
    assignPracticeToOrganizationLocation,
    saveMyOrganizationPracticeLdf,
    removeMyOrganizationPracticeLdf,
    testMyOrganizationPracticeLdf,
    saveOrganizationPracticeLdf,
    removeOrganizationPracticeLdf,
    testOrganizationPracticeLdf,
    myOrganizationPracticeLdfSubmissions,
    myOrganizationPracticeLdfSubmission,
    organizationPracticeLdfSubmissions,
    organizationPracticeLdfSubmission,
    myOrganizationPracticeLdfBackfill,
    startMyOrganizationPracticeLdfBackfill,
    organizationPracticeLdfBackfill,
    startOrganizationPracticeLdfBackfill,
    myOrganizationPracticeZorgtopicsDeliveryPreview,
    organizationPracticeZorgtopicsDeliveryPreview,
    setMyOrganizationPracticeIntakeQuestionnaires,
    setOrganizationPracticeIntakeQuestionnaires,
    saveMyOrganizationPracticeZorgmail,
    removeMyOrganizationPracticeZorgmail,
    activateMyOrganizationPracticeZorgmail,
    deactivateMyOrganizationPracticeZorgmail,
    saveOrganizationPracticeZorgmail,
    removeOrganizationPracticeZorgmail,
    activateOrganizationPracticeZorgmail,
    deactivateOrganizationPracticeZorgmail,
  };
}

export type PracticeApi = ReturnType<typeof createPracticeApi>;
